import type { QueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";

import Sentry from "@similarweb/sentry-logger";

import AIAgentTrackingService from "app/pages/sales-intelligence/modules/ai-agent/services/tracking/trackingService";
import { aiAgentKnowledgeBaseQueryKeys } from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/agent-knowledge-base/queries/queryKeyFactory";
import type { KnowledgeBaseResponse } from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/agent-knowledge-base/types";
import salesApiService from "app/pages/sales-intelligence/services/salesApiService";

// Product offerings are a single per-user resource, so merges from files processed in parallel must not interleave.
let pendingPOUpdate: Promise<unknown> = Promise.resolve();

const runPOUpdateExclusively = <T>(task: () => Promise<T>): Promise<T> => {
    const result = pendingPOUpdate.then(task);
    pendingPOUpdate = result.catch(() => undefined);
    return result;
};

export const processFile = async ({
    file,
    showMessage,
    queryClient,
    onFileUploaded,
    getPOValues,
    onPOUpdated,
}: {
    file: File;
    getPOValues: () => string[];
    onPOUpdated: (updatedPO: string[]) => void;
    showMessage: () => void;
    queryClient: QueryClient;
    onFileUploaded: () => void;
}) => {
    const formData = new FormData();
    formData.append("file", file);

    let fileId: string;

    try {
        const response = await salesApiService.aiAgent.knowledgeBase.uploadFile(formData);

        if (!response.ok) {
            throw new Error(`AI Agent Knowledge Base file upload failed with status ${response.status}`);
        }

        fileId = (await response.json()) as string;
        AIAgentTrackingService.kb.trackFileUpload(file.name, "success");
    } catch (error) {
        AIAgentTrackingService.kb.trackFileUpload(file.name, "failure");
        Sentry.error("Error uploading file to AI Agent Knowledge Base", error);
        return;
    } finally {
        onFileUploaded();
    }

    try {
        queryClient.setQueryData(
            aiAgentKnowledgeBaseQueryKeys.customization(),
            (oldData: KnowledgeBaseResponse): KnowledgeBaseResponse => ({
                ...oldData,
                files: [
                    ...oldData.files,
                    {
                        id: fileId,
                        name: file.name,
                        createdDate: dayjs(new Date()).format("YYYY-MM-DD"),
                        isProcessing: true,
                    },
                ],
            }),
        );

        const extractedPO = await salesApiService.aiAgent.knowledgeBase.getFileProductOfferings(fileId);
        AIAgentTrackingService.kb.trackPOExtracted(extractedPO);

        await runPOUpdateExclusively(async () => {
            const updatedPO = Array.from(new Set([...getPOValues(), ...extractedPO]));

            if (updatedPO.length === 0) {
                return;
            }

            await salesApiService.aiAgent.knowledgeBase.updateProductAndOfferings(updatedPO);

            onPOUpdated(updatedPO);
            queryClient.setQueryData(
                aiAgentKnowledgeBaseQueryKeys.customization(),
                (oldData: KnowledgeBaseResponse): KnowledgeBaseResponse => ({
                    ...oldData,
                    model: {
                        ...oldData.model,
                        productAndOfferings: updatedPO,
                    },
                }),
            );
        });

        showMessage();
    } catch (error) {
        Sentry.error("Error processing product offerings for AI Agent Knowledge Base file", error);
    } finally {
        queryClient.setQueryData(
            aiAgentKnowledgeBaseQueryKeys.customization(),
            (oldData: KnowledgeBaseResponse | undefined): KnowledgeBaseResponse | undefined =>
                oldData && {
                    ...oldData,
                    files: oldData.files.map((kbFile) => ({
                        ...kbFile,
                        isProcessing: kbFile.id === fileId ? false : kbFile.isProcessing,
                    })),
                },
        );
    }
};
